import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  programmeCohortService,
  ProgrammeCohortMutationPayload,
} from '@/services/programmeCohortService'
import { ProgrammeCohortModel } from '@/models/programme.model'

export const programmeCohortKeys = {
  all: ['programmeCohorts'] as const,
  lists: () => [...programmeCohortKeys.all, 'list'] as const,
  list: (programmeId: string) => [...programmeCohortKeys.lists(), programmeId] as const,
  detail: (programmeId: string, cohortId: string) =>
    [...programmeCohortKeys.list(programmeId), cohortId] as const,
}

export function useProgrammeCohorts(programmeId: string | undefined) {
  return useQuery({
    queryKey: programmeId ? programmeCohortKeys.list(programmeId) : programmeCohortKeys.lists(),
    queryFn: async (): Promise<ProgrammeCohortModel[]> => {
      if (!programmeId) throw new Error('Programme ID is required')
      return programmeCohortService.getCohorts(programmeId)
    },
    enabled: Boolean(programmeId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useCreateProgrammeCohort(programmeId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ProgrammeCohortMutationPayload) => {
      if (!programmeId) throw new Error('Programme ID is required')
      return programmeCohortService.createCohort(programmeId, payload)
    },
    onSuccess: () => {
      if (!programmeId) return
      queryClient.invalidateQueries({ queryKey: programmeCohortKeys.list(programmeId) })
    },
  })
}

export function useUpdateProgrammeCohort(
  programmeId: string | undefined,
  cohortId: string | undefined,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ProgrammeCohortMutationPayload) => {
      if (!programmeId || !cohortId) throw new Error('Programme and cohort IDs are required')
      return programmeCohortService.updateCohort(programmeId, cohortId, payload)
    },
    onSuccess: () => {
      if (!programmeId || !cohortId) return
      queryClient.invalidateQueries({ queryKey: programmeCohortKeys.list(programmeId) })
      queryClient.invalidateQueries({ queryKey: programmeCohortKeys.detail(programmeId, cohortId) })
    },
  })
}

export function useDeleteProgrammeCohort(programmeId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (cohortId: string) => {
      if (!programmeId) throw new Error('Programme ID is required')
      return programmeCohortService.deleteCohort(programmeId, cohortId)
    },
    onSuccess: (_, cohortId) => {
      if (!programmeId) return
      queryClient.invalidateQueries({ queryKey: programmeCohortKeys.list(programmeId) })
      if (cohortId) {
        queryClient.invalidateQueries({ queryKey: programmeCohortKeys.detail(programmeId, cohortId) })
      }
    },
  })
}
